import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import Spinner from './Spinner';

type Variant = 'primary' | 'accent' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'danger-outline';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: string;
  iconPosition?: 'start' | 'end';
  fullWidth?: boolean;
  children?: ReactNode;
}

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-primary-500 text-background-50 shadow-xs hover:bg-primary-600 hover:shadow-card active:bg-primary-700 disabled:bg-primary-300',
  accent:
    'bg-accent-500 text-background-50 shadow-xs hover:bg-accent-600 hover:shadow-card active:bg-accent-700 disabled:bg-accent-300',
  secondary:
    'bg-secondary-100 text-secondary-900 border border-secondary-200 hover:bg-secondary-200 disabled:opacity-60',
  outline:
    'bg-background-50 text-foreground-800 border border-background-300 hover:bg-background-100 hover:border-background-400 disabled:opacity-60',
  ghost:
    'bg-transparent text-foreground-700 hover:bg-background-100 active:bg-background-200 disabled:opacity-50',
  danger:
    'bg-rose-600 text-white shadow-xs hover:bg-rose-700 hover:shadow-card active:bg-rose-800 disabled:bg-rose-300',
  'danger-outline':
    'bg-background-50 text-rose-700 border border-rose-200 hover:bg-rose-50 hover:border-rose-300 disabled:opacity-60',
};

const SIZES: Record<Size, string> = {
  sm: 'h-9 px-3 text-sm gap-1.5',
  md: 'h-11 px-5 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2',
};

export default function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  iconPosition = 'start',
  fullWidth = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  const iconEl = icon ? <i className={cn(icon, 'text-[1.1em] leading-none')} aria-hidden="true" /> : null;

  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium transition-all duration-200 ease-smooth cursor-pointer active:translate-y-px disabled:cursor-not-allowed disabled:translate-y-0 disabled:shadow-none',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={16} /> : iconPosition === 'start' ? iconEl : null}
      {children}
      {!loading && iconPosition === 'end' ? iconEl : null}
    </button>
  );
}